import { useEffect, useState } from 'react'
import { ApiError } from '../api/client'
import { userDirectoryApi, type DirectoryUser } from '../api/userDirectory'
import { Chip, List, ListItem, TextField } from './ui'

/**
 * F-161 (TASK-0415, flag `utenti.selettore`): selettore di utenti con ricerca per nome. Il VALORE resta la stringa di
 * oggi: `"3, 12"` in modalita' `multiple`, `"7"` in modalita' singola; cosi' la pagina che lo usa non cambia logica.
 * Le etichette dei valori gia' presenti si risolvono con `ids=`. Componenti M3 (TextField, Chip, List).
 */
function parseIds(value: string): number[] {
  return value.split(',').map(s => Number(s.trim())).filter(n => Number.isInteger(n) && n > 0)
}

/**
 * Risoluzione delle etichette con UNA chiamata `ids=` per tick: tutte le richieste fatte nello stesso istante
 * (un'etichetta per riga di un pannello) si accodano e partono insieme. Cache a livello di modulo.
 */
const labelCache = new Map<number, string>()
const labelPending = new Set<number>()
const labelRequested = new Set<number>()
const labelListeners = new Set<() => void>()
let labelTimer: ReturnType<typeof setTimeout> | null = null

function flushLabels() {
  labelTimer = null
  const ids = [...labelPending]
  labelPending.clear()
  const chunks: number[][] = []
  for (let i = 0; i < ids.length; i += 200) chunks.push(ids.slice(i, i + 200))
  for (const chunk of chunks) {
    userDirectoryApi.list({ ids: chunk, activeOnly: false })
      .then(rows => {
        for (const r of rows) labelCache.set(r.id, r.name)
        labelListeners.forEach(l => l())
      })
      .catch(() => { /* senza elenco (403) resta l'id */ })
  }
}

function requestLabels(ids: number[]) {
  let any = false
  for (const i of ids) {
    if (labelCache.has(i) || labelRequested.has(i)) continue
    labelRequested.add(i)
    labelPending.add(i)
    any = true
  }
  if (any && labelTimer === null) labelTimer = setTimeout(flushLabels, 0)
}

function useUserLabels(ids: number[]): Record<number, string> {
  const [, setTick] = useState(0)
  const key = ids.join(',')
  useEffect(() => {
    const l = () => setTick(t => t + 1)
    labelListeners.add(l)
    return () => { labelListeners.delete(l) }
  }, [])
  useEffect(() => { requestLabels(ids) }, [key]) // eslint-disable-line react-hooks/exhaustive-deps
  const out: Record<number, string> = {}
  for (const i of ids) { const n = labelCache.get(i); if (n !== undefined) out[i] = n }
  return out
}

/** Nome dell'utente al posto dell'id; se non si risolve resta l'id. */
export function UserNameLabel({ id }: { id: number }) {
  const labels = useUserLabels([id])
  return <>{labels[id] ?? `${id}`}</>
}

/** F-165: l'elenco utenti e' per lo staff; l'Operatore (403) torna al campo di testo libero. Esito in cache. */
let directoryAccess: Promise<boolean> | null = null
function probeDirectoryAccess(): Promise<boolean> {
  directoryAccess ??= userDirectoryApi.list({ limit: 1 })
    .then(() => true)
    .catch(e => !(e instanceof ApiError && e.status === 403))
  return directoryAccess
}

interface Props {
  value: string
  onChange: (value: string) => void
  multiple?: boolean
  id?: string
  label?: string
  placeholder?: string
}

export function UserIdsPicker({ value, onChange, multiple = false, id, label, placeholder }: Props) {
  const [allowed, setAllowed] = useState<boolean | null>(null)
  useEffect(() => {
    let alive = true
    probeDirectoryAccess().then(ok => { if (alive) setAllowed(ok) })
    return () => { alive = false }
  }, [])
  const selected = parseIds(value)
  const labels = useUserLabels(selected)
  const [term, setTerm] = useState('')
  const [options, setOptions] = useState<DirectoryUser[]>([])
  const [searched, setSearched] = useState(false)

  useEffect(() => {
    if (allowed !== true || term.trim().length < 2) { setOptions([]); setSearched(false); return }
    let alive = true
    const t = setTimeout(() => {
      userDirectoryApi.list({ search: term.trim(), limit: 10 })
        .then(rows => { if (alive) { setOptions(rows); setSearched(true) } })
        .catch(() => { if (alive) { setOptions([]); setSearched(false) } })
    }, 250)
    return () => { alive = false; clearTimeout(t) }
  }, [term, allowed])

  const commit = (ids: number[]) => onChange(ids.join(', '))
  const pick = (u: DirectoryUser) => {
    if (multiple) commit(selected.includes(u.id) ? selected : [...selected, u.id])
    else commit([u.id])
    setTerm('')
    setOptions([])
    setSearched(false)
  }

  if (allowed === false) {
    return (
      <TextField
        id={id}
        label={label ?? (multiple ? 'Id utenti' : 'Id utente')}
        value={value}
        placeholder={placeholder ?? (multiple ? 'Id separati da virgola' : 'Id utente')}
        inputMode={multiple ? undefined : 'numeric'}
        onChange={e => onChange(e.target.value)}
      />
    )
  }

  return (
    <div className="md-stack" style={{ ['--md-stack-gap' as string]: '8px' }}>
      <TextField
        id={id}
        label={label ?? 'Cerca per nome'}
        value={term}
        placeholder={placeholder ?? 'Cerca per nome…'}
        leadingIcon="search"
        onChange={e => setTerm(e.target.value)}
        autoComplete="off"
        supportingText={searched && options.length === 0 && term.trim().length >= 2 ? 'Nessun risultato' : undefined}
      />
      {options.length > 0 && (
        <List aria-label="Utenti trovati">
          {options.map(u => (
            <ListItem key={u.id} headline={u.name} supporting={u.role} onClick={() => pick(u)} />
          ))}
        </List>
      )}
      {selected.length > 0 && (
        <div className="md-cluster">
          {selected.map(i => (
            <Chip
              key={i}
              variant="input"
              onRemove={() => commit(selected.filter(x => x !== i))}
              removeLabel={`Rimuovi ${labels[i] ?? i}`}
            >
              {labels[i] ?? `Utente ${i}`}
            </Chip>
          ))}
        </div>
      )}
    </div>
  )
}
